import { useQueryClient } from '@tanstack/react-query';
import {
  arrayOf,
  bool,
  func,
  node,
  number,
  oneOf,
  oneOfType,
  shape,
  string,
} from 'prop-types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import Typography from '@mui/material/Typography';

import { useNewfoldBrandTheme } from '~/components/MFEProvider/lib/MUI/themeDefault';
import useAMRevamp from '~/hooks/useAMRevamp';
import { useBrandInfo } from '~/hooks/useBrandInfo';
import {
  productTypes,
  setCtbPurchaseQueryData,
  useCtbProductInfo,
  useCtbPurchaseStatusQuery,
} from '~/hooks/useCtbProductInfo';
import useHostingInfo from '~/hooks/useHostingInfo';
import { getPurchaseDisabledSubBrand } from '~/utils/brand/helpers';

import useAlerts from '../Alerts/alertsStore';
import { useMFEContext } from '../MFEProvider';
import Embed from './Embed';
import getCTBEnv from './getCTBEnv';
import includeCTB, { removeCTB } from './includeCTB';

/*
  Component for ClickToBuy functionality for various supported products
  https://confluence.newfold.com/display/SF/CTB+Integration+Guide
*/

const propTypes = {
  open: bool, // controls CTB modal visibility
  product: oneOf(Object.keys(productTypes)).isRequired, // product to be purchased; see product-specific docs

  refetchData: func, // callback to refresh upgraded product info
  currentPlan: string, // initial product plan/level/tier

  // data object to be forwarded to the target CTB instance
  data: shape({
    purchaseTerm: string,
    purchaseTermUnit: string,
    parentProduct: shape({
      // Specifically added for site-based upgrades; currently only Yoast
      siteId: string,
      siteUrl: string,
    }),
    parentProdInstId: string, // used for products that are attached to a parent (such as the hosting)
    prodInstId: string, // Only used for hosting upgrades
    action: string, // Only used for hosting upgrades
    productName: string,
  }),
  disabled: bool,
  hasPurchasedRecentlyTypographyProps: shape({
    variant: string,
    fontWeight: number,
  }),
  handleCheckOrderDataCustom: func,
  handleCancelPurchase: func,
  modalWidth: oneOfType([number, string]),
  children: oneOfType([arrayOf(node), node]),
  isSitesV2: bool, // used to determine if the product is sites v2 or not
  handleDialogClose: func,
};

const CTB = ({
  open: initialOpen = false,
  product,
  handleDialogClose = () => {},

  refetchData = undefined,
  currentPlan = undefined,

  // we will forward the data object to the target CTB instance
  data = {},

  disabled = false,

  children = undefined,
  hasPurchasedRecentlyTypographyProps = {},
  handleCheckOrderDataCustom = (order) => {},
  handleCancelPurchase = () => {},
  modalWidth = '',
  isSitesV2 = false,
  // rest of props will be forwarded to the Link
  ...props
}) => {
  const { t } = useTranslation('clickToBuy', { useSuspense: false });
  const [, { generateAlert }] = useAlerts();
  const { isAMRevamp } = useAMRevamp();
  const { isDreamscape } = useBrandInfo();
  const [purchasedOrderData, setPurchasedOrderData] = useState({});

  // some black magic marketing stuff, see HOST-3192 for details
  // check query params for channelid, ex: channelid=P181C46097236S500N0B11A151D38E0000V111
  const [searchParams] = useSearchParams();
  // we need to send this into the ctb as sessionBannerId
  const sessionBannerId = searchParams.get('channelid');

  const hashFragment = '#click-to-buy-' + product;
  const { hash, pathname } = useLocation();

  const navigate = useNavigate();

  const [open, setOpen] = useState(initialOpen);
  const [ctbScriptIsIncluded, setCtbScriptIsIncluded] = useState(false);

  const [isPending, setIsPending] = useState(true);

  useEffect(() => {
    setOpen(initialOpen);
  }, [initialOpen]);

  const getHostingInfoParam = () => {
    if (
      isAMRevamp &&
      !(
        product.includes(productTypes.CODEGUARD_UPGRADE) ||
        product.includes(productTypes.JETPACK_BACKUP_T1) ||
        product.includes(productTypes.SITELOCK_UPGRADE) ||
        product.includes(productTypes.WP_SOLUTION_FAMILY) ||
        product.includes(productTypes.CPANEL_UPGRADE) ||
        product.includes(productTypes.BH_BACKUP_UPGRADE) ||
        product.includes(productTypes.BH_PREM_BKUP_ADDL_STORAGE) ||
        product.includes(productTypes.XCITIUM_UPGRADE) ||
        product.includes(productTypes.MONARX_FAMILY)
      )
    ) {
      return { hostingId: data?.prodInstId };
    }
    return undefined;
  };

  const hostingInfoParam = getHostingInfoParam();
  const {
    hostingAccount,
    isDisabled: isHostingDisabled,
    isSuspended,
  } = useHostingInfo(hostingInfoParam);

  const subbrand = hostingAccount?.data?.billing?.subbrand;
  const isDisabled = isHostingDisabled || disabled;
  const isExcludedSubbrand = getPurchaseDisabledSubBrand(subbrand);
  const isLinkDisabled = isSuspended || isDisabled;

  const handleClose = () => {
    // As this handler gets called for both Confirmation close and Dialog X icon. Check if CTB dialog X icon is clicked, which means no purchase
    if (Object.keys(purchasedOrderData).length === 0) {
      handleCancelPurchase();
    } else {
      // Fix for ctb deeplink email marketing campaign.
      if (hash.includes(hashFragment)) navigate(pathname);
    }
    setOpen(false);
    handleDialogClose();
  };

  const {
    accessToken,
    /* We want to use the default refresh callback from AMM here instead of our
      overloaded one, since the CTB shouldn't be setting a new token in our app */
    callbacks: { refreshAccessToken, handleClickToBuy },
    brandFromJWT: brand,
  } = useMFEContext();
  const { env } = getCTBEnv(accessToken);
  const productInfo = useCtbProductInfo(
    product,
    data?.prodInstId,
    data?.hostingData?.productName,
    data?.used_bytes,
    data?.allowed_limit_bytes,
  );
  const queryClient = useQueryClient();

  const theme = useNewfoldBrandTheme();

  const { hasPurchasedRecently, isLoading } = useCtbPurchaseStatusQuery(
    productInfo.purchaseQueryKey,
    // refetchData // eventually refetch actual product info
  );

  useEffect(() => {
    if (accessToken) {
      includeCTB(accessToken);
      setCtbScriptIsIncluded(true);
    }

    return () => {
      setCtbScriptIsIncluded(false);
      removeCTB();
    };
  }, [accessToken]);

  const handleOpenCtbModal = (e) => {
    // Ignore clicks when link is disabled/suspended
    e.preventDefault();
    if (isLinkDisabled) {
      e.stopPropagation();
      return;
    }
    setOpen(true);
  };

  const closeCtbModal = () => {
    return handleClose();
  };

  const checkOrderDetails = (orderId) => {
    setCtbPurchaseQueryData(queryClient, productInfo.purchaseQueryKey, {
      hasPurchasedRecently: true,
    });
    generateAlert({
      description: t('alerts.upgradeSuccess', {
        product: productInfo.name,
      }),
      severity: 'success',
      showCloseBtn: true,
    });
  };

  const setLoader = (loadingStatus) => {
    setIsPending(loadingStatus);
  };

  const handleInitErrors = (initError) => {
    closeCtbModal();
    generateAlert({
      description: t('alerts.initError', {
        product: productInfo.name,
      }),
      severity: 'error',
      showCloseBtn: true,
      autoHideDuration: 10000,
    });
  };
  const onOrderSubmitted = (orderData) => {
    if (handleCheckOrderDataCustom) {
      setPurchasedOrderData(orderData);
      handleCheckOrderDataCustom(orderData);
      return;
    }
  };
  const StyledTypographyLink = styled(Link)({
    textDecoration: 'none',
    '&:hover, &:focus, &:active': {
      textDecoration: 'none',
    },
  });

  const ctbCallbacks = {
    closeCtbModal,
    refreshAccessToken,
    checkOrderDetails,
    setLoader,
    handleInitErrors,
    onOrderSubmitted,
  };

  const ctbData = {
    // ALWAYS pass the following values
    accessToken,
    brand,
    env,
    sessionBannerId,

    // CONDITIONALLY apply default data for the given product
    // check that the product exists
    ...(productInfo
      ? // if so spread its values into this object
        productInfo.data
      : // else spread an empty object (nothing)
        {}),

    // ALLOW <CTB /> CONSUMER to apply any overrides
    ...data,
    // Pass accountId since Revamp UI uses user level token
    ...(isAMRevamp && {
      accountId: hostingAccount?.data?.account_id,
    }),
  };

  useEffect(() => {
    if (
      ctbScriptIsIncluded &&
      !hasPurchasedRecently &&
      !isLoading &&
      hash.includes(hashFragment)
    ) {
      setOpen(true);
    }
  }, [
    ctbScriptIsIncluded,
    hasPurchasedRecently,
    hash,
    isLoading,
    hashFragment,
  ]);

  // excluded subbrands should NOT get the override option below
  if (isExcludedSubbrand) return null;

  // OVERRIDE if handler is provided
  if (
    handleClickToBuy &&
    handleClickToBuy[product] &&
    typeof handleClickToBuy[product] === 'function'
  ) {
    return (
      <Link
        id={hashFragment}
        href={hashFragment}
        onClick={(e) => {
          handleClickToBuy[product](e);
          handleDialogClose();
        }}
        data-testid={`${product}-ctb-link`}
        data-ctb-link="true"
        {...(isLinkDisabled && {
          'aria-disabled': true,
          tabIndex: -1,
        })}
        {...props}
        sx={{
          ...(props?.sx || {}),
          ...(isLinkDisabled
            ? {
                pointerEvents: 'none',
                cursor: 'not-allowed',
                color: 'text.disabled',
              }
            : {}),
        }}
      >
        {children}
      </Link>
    );
  }

  // If no handleClickToBuy callback is provided for Dreamscape, return null
  if (isDreamscape) return null;

  // Jarvis Global CTB
  return (
    <>
      {/* return children as links */}
      {children &&
        !hasPurchasedRecently &&
        (isSitesV2 ? (
          <StyledTypographyLink
            id={hashFragment}
            href={hashFragment}
            onClick={handleOpenCtbModal}
            data-testid={`${product}-ctb-link`}
            data-ctb-link="true"
            {...(isLinkDisabled && {
              'aria-disabled': true,
              tabIndex: -1,
            })}
            {...props}
            sx={{
              ...(props?.sx || {}),
              ...(isLinkDisabled
                ? {
                    pointerEvents: 'none',
                    cursor: 'not-allowed',
                    color: 'text.disabled',
                  }
                : {}),
            }}
          >
            {children}
          </StyledTypographyLink>
        ) : (
          <Link
            id={hashFragment}
            href={hashFragment}
            onClick={handleOpenCtbModal}
            data-testid={`${product}-ctb-link`}
            data-ctb-link="true"
            {...(isLinkDisabled && {
              'aria-disabled': true,
              tabIndex: -1,
            })}
            {...props}
            sx={{
              ...(props?.sx || {}),
              ...(isLinkDisabled
                ? {
                    pointerEvents: 'none',
                    cursor: 'not-allowed',
                    color: 'text.disabled',
                  }
                : {}),
            }}
          >
            {children}
          </Link>
        ))}
      {hasPurchasedRecently && children && (
        <Typography
          noWrap
          color={'text.placeholder'}
          variant="body2"
          {...hasPurchasedRecentlyTypographyProps}
        >
          <Stack
            direction={'row'}
            spacing={1}
            data-testid={`${product}-ctb-upgrading`}
            alignItems={'center'}
            component={'span'}
          >
            <CircularProgress size={16} color="inherit"></CircularProgress>
            <span>{t('labels.upgrading')}</span>
          </Stack>
        </Typography>
      )}

      {open && (
        <ThemeProvider theme={theme}>
          <Dialog
            maxWidth="xl"
            onClose={closeCtbModal}
            aria-labelledby="customized-dialog-title"
            open={open}
            data-testid="click-to-buy"
            sx={{
              ...(modalWidth && {
                '& .MuiDialog-paper': {
                  width: modalWidth,
                },
              }),
              '@media (max-width: 500px)': {
                '& .MuiDialog-paper': {
                  margin: 0,
                },
              },
            }}
          >
            <DialogContent
              id="click-to-buy"
              sx={{ minHeight: '100%', minWidth: '100%', padding: 0 }}
            >
              <Embed
                callbacks={ctbCallbacks}
                data={ctbData}
                isPending={isPending}
                product={product}
              />
            </DialogContent>
          </Dialog>
        </ThemeProvider>
      )}
    </>
  );
};

CTB.propTypes = propTypes;
export default CTB;
